/* global BigInt */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store';
import API from '../api';
import { useLang, LangToggle } from '../i18n';

const COINS_PER_CHEST = 100_000n;

function toCoinBig(v) {
  try { return BigInt(String(v ?? 0)); } catch { return 0n; }
}
function fmtUsdt(v, digits = 6) {
  const n = parseFloat(String(v || 0));
  return n.toFixed(digits);
}
function statusText(s) {
  return { pending:'待领取', processing:'确认中', success:'已到账' }[s] || s;
}
function makeKey() {
  return window.crypto?.randomUUID?.() || `wd_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/* ── 宝箱动画组件 ── */
function ChestModal({ onClose, onOpen, chestCount, usdtWon, opening, lang = 'zh' }) {
  const W = (zh, en) => lang === 'zh' ? zh : en;
  const [phase, setPhase] = useState('idle'); // idle | shaking | exploding | reward
  const [particles, setParticles] = useState([]);
  const shakeRef = useRef(null);

  const triggerOpen = useCallback(async () => {
    if (phase !== 'idle' || opening) return;
    setPhase('shaking');
    shakeRef.current = setTimeout(async () => {
      setPhase('exploding');
      setParticles(Array.from({ length: 24 }, (_, i) => ({
        id: i,
        angle: (i / 24) * 360,
        dist: 60 + Math.random() * 80,
        size: 4 + Math.random() * 8,
        color: ['#F5C518','#FFD700','#fff','#2ECC71','#2E7FE8'][i % 5],
        dur: 0.6 + Math.random() * 0.4,
      })));
      await onOpen();
      setTimeout(() => {
        setPhase('reward');
        setParticles([]);
      }, 800);
    }, 900);
  }, [phase, opening, onOpen]);

  useEffect(() => () => clearTimeout(shakeRef.current), []);

  const won = parseFloat(usdtWon || 0);

  const chestStyle = {
    fontSize: 100,
    display: 'block',
    lineHeight: 1,
    transition: 'transform 0.1s',
    animation: phase === 'shaking' ? 'chestShake 0.15s infinite' : 'none',
    transform: phase === 'exploding' ? 'scale(1.5)' : 'scale(1)',
    opacity: phase === 'exploding' ? 0 : 1,
    filter: phase === 'shaking' ? 'drop-shadow(0 0 20px #F5C518)' : 'none',
    cursor: phase === 'idle' ? 'pointer' : 'default',
  };

  return (
    <div style={{
      position:'fixed',inset:0,zIndex:1000,
      background:'rgba(5,12,35,0.92)',
      display:'flex',alignItems:'center',justifyContent:'center',
      flexDirection:'column',
    }}>
      <style>{`
        @keyframes chestShake {
          0%   { transform:rotate(-8deg) scale(1.05); }
          25%  { transform:rotate(8deg)  scale(1.1);  }
          50%  { transform:rotate(-6deg) scale(1.05); }
          75%  { transform:rotate(6deg)  scale(1.1);  }
          100% { transform:rotate(-8deg) scale(1.05); }
        }
        @keyframes particle {
          0%   { transform:translate(0,0) scale(1); opacity:1; }
          100% { transform:translate(var(--tx),var(--ty)) scale(0); opacity:0; }
        }
        @keyframes rewardPop {
          0%   { transform:scale(0.4); opacity:0; }
          70%  { transform:scale(1.12); opacity:1; }
          100% { transform:scale(1);   opacity:1; }
        }
        @keyframes goldGlow {
          0%,100% { text-shadow:0 0 20px #F5C51888; }
          50%     { text-shadow:0 0 50px #F5C518,0 0 80px #FFD700aa; }
        }
      `}</style>

      {phase === 'reward' ? (
        <div style={{textAlign:'center',animation:'rewardPop 0.5s ease-out forwards'}}>
          <div style={{fontSize:80,marginBottom:8}}>
            {won >= 0.002 ? '💎' : won >= 0.001 ? '🥇' : '💰'}
          </div>
          <div style={{fontSize:13,color:'#8BA8D4',marginBottom:6,letterSpacing:2}}>{W('恭喜获得','You Got')}</div>
          <div style={{
            fontFamily:'Share Tech Mono',fontSize:48,fontWeight:900,
            color:'#F5C518',lineHeight:1,
            animation:'goldGlow 1.5s infinite',
          }}>
            +{won.toFixed(6)}
          </div>
          <div style={{fontSize:18,color:'#fff',marginTop:6,fontWeight:700}}>USDT</div>
          <div style={{fontSize:12,color:'#8BA8D4',marginTop:4}}>BNB Chain</div>
          <div style={{marginTop:24,display:'flex',gap:12,justifyContent:'center'}}>
            {chestCount > 0 && (
              <button onClick={() => { setPhase('idle'); }} style={{
                padding:'12px 24px',borderRadius:12,border:'none',cursor:'pointer',
                background:'linear-gradient(180deg,#FFD700,#E6A800)',
                color:'#000',fontWeight:800,fontSize:14,
                boxShadow:'0 4px 0 #8B6200',
              }}>
                {W(`再开一个（剩余 ${chestCount}）`,`Open Another (${chestCount} left)`)}
              </button>
            )}
            <button onClick={onClose} style={{
              padding:'12px 24px',borderRadius:12,border:'none',cursor:'pointer',
              background:'rgba(46,127,232,0.2)',color:'#fff',fontWeight:700,fontSize:14,
            }}>
              {W('收下并关闭','Close')}
            </button>
          </div>
        </div>
      ) : (
        <div style={{textAlign:'center',position:'relative'}}>
          {particles.map(p => {
            const tx = Math.cos(p.angle * Math.PI / 180) * p.dist;
            const ty = Math.sin(p.angle * Math.PI / 180) * p.dist;
            return (
              <div key={p.id} style={{
                position:'absolute',top:'50%',left:'50%',
                width:p.size,height:p.size,borderRadius:'50%',
                background:p.color,
                animation:`particle ${p.dur}s ease-out forwards`,
                '--tx':`${tx}px`,'--ty':`${ty}px`,
              }}/>
            );
          })}

          <div style={{fontSize:14,color:'#8BA8D4',marginBottom:16,letterSpacing:2}}>
            {phase === 'idle' ? W('点击宝箱开启','Tap to Open') : phase === 'shaking' ? W('正在开启...','Opening...') : ''}
          </div>

          <span style={chestStyle} onClick={triggerOpen} role="button">
            {phase === 'exploding' ? '✨' : '📦'}
          </span>

          <div style={{marginTop:20,fontSize:13,color:'#8BA8D4'}}>
            {W('每箱随机掉落','Random drop: ')}<span style={{color:'#F5C518',fontWeight:700}}>USDT (BNB Chain)</span>
          </div>

          {phase === 'idle' && (
            <button onClick={onClose} style={{
              marginTop:24,padding:'10px 28px',borderRadius:12,border:'none',cursor:'pointer',
              background:'rgba(255,255,255,0.06)',color:'#8BA8D4',fontSize:13,
            }}>{W('取消','Cancel')}</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── 主页面 ── */
export default function Withdraw() {
  const { user } = useApp();
  const { lang } = useLang();
  const nav = useNavigate();
  const zh = lang === 'zh';
  const W = (zhStr, enStr) => zh ? zhStr : enStr;

  const [status, setStatus]   = useState(null);
  const [msg, setMsg]         = useState('');
  const [loading, setL]       = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHL]  = useState(true);

  const [showChest, setShowChest] = useState(false);
  const [opening,   setOpening]   = useState(false);
  const [lastWon,   setLastWon]   = useState(null);

  const [address, setAddress] = useState('');
  const [wdLoading, setWdL]   = useState(false);
  const [chestHistory, setChestHistory] = useState([]);

  const points = toCoinBig(status?.points ?? user?.points);
  const chestAvail = status?.chestAvailable ?? 0;
  const usdtBal = parseFloat(status?.usdtBalance ?? 0); // 后端兼容字段
  const chestsCanBuy = Number(points / COINS_PER_CHEST);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  async function fetchAll() {
    setHL(true);
    try {
      const [s, h, ch] = await Promise.all([
        API.get('/api/chest/status'),
        API.get('/api/withdraw'),
        API.get('/api/chest/history'),
      ]);
      setStatus(s);
      setHistory(h.list || []);
      setChestHistory(ch.list || []);
    } catch { /* ignore */ }
    finally { setHL(false); }
  }

  async function buyChest() {
    if (chestsCanBuy < 1 || loading) return;
    setL(true);
    try {
      const r = await API.post('/api/chest/exchange', { count: 1 });
      setStatus({ points: r.points, chestAvailable: r.chestAvailable, usdtBalance: r.usdtBalance });
      setShowChest(true);
      setLastWon(null);
    } catch(e) { setMsg(`ERR:${e.message}`); }
    finally { setL(false); }
  }

  async function doOpen() {
    setOpening(true);
    try {
      const r = await API.post('/api/chest/open', {});
      setStatus({ points: r.points, chestAvailable: r.chestAvailable, usdtBalance: r.usdtBalance });
      setLastWon(r.usdtWon); // 后端兼容字段
      return r.usdtWon;
    } catch(e) { setMsg(`ERR:${e.message}`); setShowChest(false); return null; }
    finally { setOpening(false); }
  }

  function closeChest() {
    setShowChest(false);
    setLastWon(null);
    fetchAll();
  }

  async function submitWithdraw(e) {
    e.preventDefault();
    if (!address.startsWith('0x') || address.length !== 42) { setMsg('ERR:请输入正确的 BSC 地址（0x...）'); return; }
    if (usdtBal <= 0) { setMsg('ERR:没有可提现的 USDT'); return; }
    setWdL(true); setMsg('');
    try {
      const d = await API.post('/api/chest/withdraw', { address, idempotencyKey: makeKey() });
      const url = d.withdrawUrl || d.withdrawal?.withdrawUrl;
      setMsg('OK:正在跳转 PayTheFly');
      setStatus(s => ({ ...s, usdtBalance: '0' }));
      await fetchAll();
      if (url) window.location.href = url;
    } catch(e) { setMsg(`ERR:${String(e?.message || e)}`); }
    finally { setWdL(false); }
  }

  const S = `
    *{box-sizing:border-box;margin:0;padding:0;}
    .wr{min-height:100vh;
      background:linear-gradient(180deg,#0B1E4A 0%,#0D2B6B 50%,#091840 100%);
      background-attachment:fixed;padding-bottom:60px;}
    .wh{display:flex;align-items:center;gap:14px;padding:52px 20px 20px;
      border-bottom:1px solid rgba(46,127,232,0.15);}
    .wh-back{background:rgba(46,127,232,0.1);border:none;color:#8BA8D4;
      font-size:18px;cursor:pointer;padding:8px 12px;border-radius:10px;}
    .wh-title{font-size:20px;font-weight:800;color:#fff;}
    .wb{padding:18px 16px;}
    .wcard{background:#0F2554;border-radius:16px;padding:18px;margin-bottom:14px;}
    .wlabel{font-size:11px;color:#8BA8D4;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;}
    .wbig{font-family:'Share Tech Mono',monospace;font-size:32px;font-weight:900;color:#F5C518;}
    .wsub{font-size:12px;color:#8BA8D4;margin-top:4px;}
    .btn-gold{width:100%;padding:13px;border-radius:12px;border:none;cursor:pointer;
      font-size:14px;font-weight:800;
      background:linear-gradient(180deg,#FFD700 0%,#E6A800 100%);color:#000;
      box-shadow:0 4px 0 #8B6200;}
    .btn-gold:active{transform:translateY(3px);box-shadow:0 1px 0 #8B6200;}
    .btn-gold:disabled{opacity:0.4;cursor:not-allowed;transform:none;box-shadow:none;}
    .btn-blue{width:100%;padding:13px;border-radius:12px;border:none;cursor:pointer;
      font-size:14px;font-weight:800;
      background:linear-gradient(180deg,#2E7FE8 0%,#1a5fc4 100%);color:#fff;
      box-shadow:0 4px 0 #0f3d8a;}
    .btn-blue:active{transform:translateY(3px);box-shadow:0 1px 0 #0f3d8a;}
    .btn-blue:disabled{opacity:0.4;cursor:not-allowed;transform:none;box-shadow:none;}
    .wmsg-ok{padding:10px 14px;border-radius:10px;font-size:13px;
      background:rgba(46,204,113,0.1);color:#2ECC71;margin-bottom:12px;}
    .wmsg-err{padding:10px 14px;border-radius:10px;font-size:13px;
      background:rgba(232,69,60,0.1);color:#E8453C;margin-bottom:12px;}
    .wh-row{padding:12px;border-radius:12px;background:rgba(46,127,232,0.07);margin-bottom:8px;}
    .wh-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;}
    .wh-amt{font-family:'Share Tech Mono',monospace;color:#fff;font-size:14px;font-weight:800;}
    .wh-st{font-size:13px;font-weight:800;color:#F5C518;}
    .wh-st-success{font-size:13px;font-weight:800;color:#2ECC71;}
    .wh-meta{font-size:11px;color:#8BA8D4;line-height:1.6;word-break:break-all;}
    .wlink{margin-top:7px;padding:7px 10px;border-radius:9px;border:none;
      background:rgba(245,197,24,0.12);color:#F5C518;font-size:12px;font-weight:700;cursor:pointer;}
    .chest-counter{display:flex;align-items:center;justify-content:space-between;
      background:rgba(245,197,24,0.08);border-radius:12px;padding:14px 16px;margin-bottom:12px;}
    .input{width:100%;padding:12px 14px;border-radius:10px;border:1px solid rgba(46,127,232,0.3);
      background:rgba(15,37,84,0.8);color:#fff;font-size:14px;outline:none;}
    .input::placeholder{color:#4A6A9A;}
  `;

  return (
    <div className="wr">
      <style>{S}</style>

      {showChest && (
        <ChestModal
          onClose={closeChest}
          onOpen={doOpen}
          chestCount={chestAvail}
          usdtWon={lastWon}
          opening={opening}
          lang={lang}
        />
      )}

      <div className="wh">
        <button className="wh-back" onClick={() => nav('/lobby')}>&#8592;</button>
        <div className="wh-title">{W('宝箱 & 提现','Chest & Withdraw')}</div>
        <LangToggle />
      </div>

      <div className="wb">
        {msg && (
          <div className={msg.startsWith('OK:') ? 'wmsg-ok' : 'wmsg-err'}>
            {msg.replace(/^(OK:|ERR:)/,'')}
          </div>
        )}

        {/* 积分余额 */}
        <div className="wcard">
          <div className="wlabel">{W('游戏积分','Game Points')}</div>
          <div className="wbig">{Number(points).toLocaleString()}</div>
          <div className="wsub">{W('COIN · 每 10万 COIN 可兑换 1 个宝箱','COIN · 100K COIN = 1 Chest')}</div>
        </div>

        {/* 宝箱区 */}
        <div className="wcard">
          <div className="wlabel" style={{marginBottom:12}}>{W('神秘宝箱','Mystery Chest')}</div>

          <div className="chest-counter">
            <div>
              <div style={{fontSize:13,color:'#fff',fontWeight:700}}>
                {W('可用宝箱：','Available Chests: ')}<span style={{color:'#F5C518',fontFamily:'Share Tech Mono',fontSize:20}}>{chestAvail}</span>{W(' 个','')}
              </div>
              <div style={{fontSize:11,color:'#8BA8D4',marginTop:2}}>
                {W('可兑换：','Redeemable: ')}{chestsCanBuy}{W(' 个（',' (')} {Number(COINS_PER_CHEST).toLocaleString()}{W(' COIN/个）',' COIN each)')}
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:10,marginBottom:12}}>
            <button className="btn-gold" style={{flex:1}}
              disabled={chestsCanBuy < 1 || loading}
              onClick={buyChest}>
              {loading ? W('兑换中...','Redeeming...') : `${W('兑换宝箱（-','Redeem Chest (-')}${Number(COINS_PER_CHEST).toLocaleString()} COIN${W('）',')')}`}
            </button>
            <button className="btn-blue" style={{flex:1}}
              disabled={chestAvail < 1 || loading}
              onClick={() => { if (chestAvail > 0) setShowChest(true); }}>
              {W('开启宝箱','Open Chest')}{chestAvail > 0 ? ` (${chestAvail})` : ''}
            </button>
          </div>

          <div style={{fontSize:11,color:'#8BA8D4',lineHeight:1.7,textAlign:'center'}}>
            {W('每个宝箱必出 USDT（BNB Chain），可立即提现到钱包','Every chest drops USDT (BNB Chain), withdraw instantly')}
          </div>
        </div>

        {/* USDT 余额 & 提现 */}
        <div className="wcard">
          <div className="wlabel">{W('USDT 余额（可随时提现）','USDT Balance (withdraw anytime)')}</div>
          <div className="wbig" style={{fontSize:28}}>{fmtUsdt(usdtBal, 6)}</div>
          <div className="wsub">USDT · BNB Chain</div>

          {usdtBal > 0 && (
            <form onSubmit={submitWithdraw} style={{marginTop:16,display:'flex',flexDirection:'column',gap:10}}>
              <input className="input" type="text"
                placeholder={W('收款 BSC 地址（0x...）','BSC Wallet Address (0x...)')}
                value={address} onChange={e=>setAddress(e.target.value)} required />
              <button className="btn-gold" type="submit" disabled={wdLoading}>
                {wdLoading ? W('提交中...','Submitting...') : `${W('提现全部 ','Withdraw All ')}${fmtUsdt(usdtBal,6)} USDT`}
              </button>
            </form>
          )}
          {usdtBal <= 0 && (
            <div style={{fontSize:12,color:'#4A6A9A',marginTop:10}}>{W('开宝箱获得 USDT 后才可提现','Open chests to earn USDT first')}</div>
          )}
        </div>

        <div className="wcard">
          <div className="wlabel" style={{marginBottom:12}}>{W('开箱记录','Chest History')}</div>
          {chestHistory.length === 0 ? (
            <div style={{fontSize:13,color:'#8BA8D4'}}>{W('暂无开箱记录','No chest history')}</div>
          ) : chestHistory.map(r => (
            <div key={r.id} style={{
              display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'10px 12px',borderRadius:10,marginBottom:6,
              background:'rgba(46,127,232,0.07)',
            }}>
              <div>
                <div style={{fontSize:12,color:'#8BA8D4'}}>{new Date(r.created_at).toLocaleString(lang==='en'?'en-US':'zh-CN')}</div>
                <div style={{fontSize:11,color:'#4A6A9A',marginTop:2}}>{W('消耗 ','Spent ')}{Number(r.coins_spent).toLocaleString()} COIN</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontFamily:'Share Tech Mono',fontSize:15,fontWeight:900,color:'#F5C518'}}>
                  +{parseFloat(r.usdt_won).toFixed(6)}
                </div>
                <div style={{fontSize:11,color:'#8BA8D4'}}>USDT</div>
              </div>
            </div>
          ))}
        </div>

        <div className="wcard">
          <div className="wlabel" style={{marginBottom:12}}>{W('提现记录','Withdraw History')}</div>
          {histLoading ? (
            <div style={{fontSize:13,color:'#8BA8D4'}}>{W('加载中...','Loading...')}</div>
          ) : history.length === 0 ? (
            <div style={{fontSize:13,color:'#8BA8D4'}}>{W('暂无提现记录','No withdrawal records')}</div>
          ) : history.map(w => (
            <div className="wh-row" key={w.serialNo}>
              <div className="wh-top">
                <div className="wh-amt">{w.amountUsdt} USDT</div>
                <div className={w.status==='success'?'wh-st-success':'wh-st'}>{statusText(w.status)}</div>
              </div>
              <div className="wh-meta">{w.walletAddress}</div>
              <div className="wh-meta">{new Date(w.createdAt).toLocaleString(lang==='en'?'en-US':'zh-CN')} · {w.serialNo}</div>
              {w.status !== 'success' && (
                <button className="wlink" onClick={() => { window.location.href = w.withdrawUrl; }}>{W('重新打开提现链接','Reopen Withdraw Link')}</button>
              )}
              {w.status === 'success' && w.txHash && (
                <button className="wlink" onClick={() => window.open(`https://bscscan.com/tx/${w.txHash}`, '_blank', 'noopener')}>{W('查看链上交易','View on BscScan')}</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
